import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Select, { SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
}

const mapStateToProps = (state: CustomApplicationState, props: OwnProps) => ({
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
    billToParentState: state.ADIGlobalData.ADIBillToStore.billToParentState,
});

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    container?: InjectableCss;
    title?: TypographyProps;
    selectStyles?: SelectProps;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {
    container: {
        css: css`
            width: 100%;

            [class^="FormInputWrapper-"] {
                margin-top: 0 !important;
            }
        `,
    },
    title: {
        css: css`
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            text-transform: capitalize;
            font-weight: 700;
            margin-bottom: 7px;
            display: block;
        `,
    },
    selectStyles: {
        css: css`
            border: 1px solid ${ADIColors.gray15};
            border-radius: 0 !important;
            font-size: 0.875rem;
            height: 2.7em;
            padding: 0.6em 1.2em 0.6em 0.5em;
            transition: border-color 0.45s ease-in-out 0s;
            box-shadow: none;
            font-family: inherit;
            padding: 9px;
            option {
                font-size: 0.875rem;
            }
        `,
    },
};

const ChangeCustomerBillToSelector: FC<Props> = ({ onSelect, extendedStyles, billToParentState }) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedBillTo, setSelectedBillTo] = useState("");

    useEffect(() => {
        const billTo = billToParentState?.billTos.filter(
            billTo => billTo.customerNumber === billToParentState.properties.currentCustomerNumber,
        )[0];
        if (billTo) {
            setSelectedBillTo(billTo.id);
            onSelect(billTo as unknown as BillToModel);
        }
    }, [billToParentState?.billTos]);

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.target.value) {
            const billTo = billToParentState?.billTos.filter(billTo => billTo.id === event.target.value)[0];
            if (billTo) {
                onSelect(billTo as unknown as BillToModel);
                setSelectedBillTo(billTo.id);
            }
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{translate("Select Account")}</Typography>
            <Select onChange={handleSelectChange} value={selectedBillTo} {...styles.selectStyles}>
                {billToParentState?.billTos.map(billTo => (
                    <option value={billTo.id}>{billTo.label}</option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
